import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */
/** @typedef {{ variant: string | undefined, place: number, of: number }} RowName */

/**
 * REAL FUEL 2026-11-01: a request that never answers must not freeze the cart, so it gives up after 20 seconds.
 * @param {string | URL} url - The URL.
 * @param {RequestInit} [options] - The fetch options.
 * @returns {Promise<{ ok: boolean, status: number, text: string }>} The answer. Rejects with noAnswer set if the store never answered.
 */
async function fetchWithin(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });

    return { ok: response.ok, status: response.status, text: await response.text() };
  } catch (error) {
    throw Object.assign(new Error('No answer'), { noAnswer: true, cause: error });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @param {HTMLElement} row - A cart row.
 * @returns {string | undefined} The row's variant id, the part of its key that stays the same.
 */
const variantOf = (row) => row.dataset.key?.split(':')[0];

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  // REAL FUEL 2026-11-01: stock Horizon shared one 300 ms debounce across every row, so a tap on a second
  // row dropped the first, and a change still waiting when trash was tapped landed on whichever line moved
  // up. Now each row keeps its own waiting change, taps made while a request is out add up, and requests go
  // out one at a time, each finding its row by name when it is sent.
  /** @type {Map<string, { name: RowName, quantity: number }>} */
  #waiting = new Map();
  // Changes and removals that have left #waiting but not gone out yet, behind a request that is out.
  /** @type {Set<{ name: RowName, quantity: number }>} */
  #queued = new Set();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #waitTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #liveCheckTimer;
  #requests = 0;
  /** @type {Promise<unknown>} */
  #chain = Promise.resolve();
  // Goes up when the page turns out to be out of date, so changes queued from its old rows don't go out.
  #pageVersion = 0;
  #checkoutWaiting = false;
  // A job failed in this run of jobs, so a waiting Checkout tap stops and the shopper sees the page first.
  #stopCheckout = false;
  // Gave up with no signal, so the page shows what it last knew. The next Checkout tap redraws first.
  #unsure = false;
  #tileTapUntil = 0;
  // A removal just moved the rows from this one down, and the next flavour sits under a finger that may still be tapping.
  #movedFrom = 0;
  #movedUntil = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueChange);
    document.addEventListener('visibilitychange', this.#checkLiveCart);
    window.addEventListener('online', this.#checkLiveCart);
    window.addEventListener('pageshow', this.#onPageShow);
    this.addEventListener('click', this.#onClick, true);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('formdata', this.#onFormData);

    // A reload during a change can draw the cart from just before the change landed.
    this.#liveCheckTimer = setTimeout(this.#checkLiveCart, 1500);
    this.#queueEarlyBoxes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueChange);
    document.removeEventListener('visibilitychange', this.#checkLiveCart);
    window.removeEventListener('online', this.#checkLiveCart);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.removeEventListener('click', this.#onClick, true);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('formdata', this.#onFormData);
    clearTimeout(this.#waitTimer);
    clearTimeout(this.#liveCheckTimer);
  }

  /** @returns {HTMLElement[]} The rows on the page. Not refs: a row taken off the end stays in refs. */
  get #rows() {
    return [...this.querySelectorAll('[ref="cartItemRows[]"]')].filter((row) => row instanceof HTMLElement);
  }

  /**
   * Names a row so a redraw that moves the rows can't point a change at another flavour. A key can't do it, because it
   * changes when Stack & Save moves. The variant can, with the row's place for the rare cart with two lines of a variant.
   * @param {HTMLElement} row - The row.
   * @returns {RowName} The name.
   */
  #nameRow(row) {
    const variant = variantOf(row);
    const same = this.#rows.filter((other) => variantOf(other) === variant);

    return { variant, place: same.indexOf(row), of: same.length };
  }

  /**
   * @param {RowName} name - The row's name.
   * @returns {number} The row's line right now, or 0 if it has gone or rows of its variant came or went.
   */
  #lineOf({ variant, place, of }) {
    const rows = this.#rows;
    const same = rows.filter((row) => variantOf(row) === variant);
    const row = same[place];

    return same.length === of && row ? rows.indexOf(row) + 1 : 0;
  }

  /**
   * @param {number} line - A row's line.
   * @returns {boolean} Whether a removal moved this row under the finger a moment ago. A finger still tapping keeps this
   * going, so a burst of taps takes out one row.
   */
  #justMoved(line) {
    if (performance.now() >= this.#movedUntil || line - 1 < this.#movedFrom) return false;

    this.#movedUntil = performance.now() + 400;

    return true;
  }

  /**
   * @returns {boolean} Whether a removal is still sliding things up the page, so what's under a finger may be new there.
   * For what sits below the rows, like the Delivery switch.
   */
  settling() {
    return this.#justMoved(Number.MAX_SAFE_INTEGER);
  }

  /**
   * REAL FUEL 2026-11-02: a tap or typed number from before this script ran had nobody to hear it, and Checkout no
   * longer posts the boxes, so it goes now. A box still being typed in queues itself when it is left.
   */
  #queueEarlyBoxes() {
    const typing = this.#typedBox();

    this.#rows.forEach((row, index) => {
      const input = row.querySelector('input[name="updates[]"]');

      if (!(input instanceof HTMLInputElement) || input === typing || input.value === input.defaultValue) return;

      const quantity = parseInt(input.value, 10);

      if (quantity >= 0) this.#queueChange(new QuantitySelectorUpdateEvent(quantity, index + 1));
      else input.value = input.defaultValue;
    });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #queueChange = (event) => {
    const { quantity, cartLine: line } = event.detail;
    const row = line ? this.#rows[line - 1] : null;
    const input = row?.querySelector('input[name="updates[]"]');

    if (!row || !(input instanceof HTMLInputElement)) return;
    // A row on its way out takes no more taps. Queued, a tap would find no row, and that throws away the other rows' taps.
    if (row.classList.contains('removing') || row.style.display === 'none') return;

    const name = this.#nameRow(row);
    const id = `${name.variant}:${name.place}`;

    if (this.#justMoved(line)) {
      input.value = String(this.#waiting.get(id)?.quantity ?? input.defaultValue);
      return;
    }

    // Leaving a box without changing it is not a change. While a request is out the store's number is about to move,
    // so the same number can be a change.
    if (!this.#requests && !this.#waiting.has(id) && String(quantity) === input.defaultValue) {
      input.value = input.defaultValue;
      return;
    }

    this.#waiting.set(id, { name, quantity });
    clearTimeout(this.#waitTimer);
    this.#waitTimer = setTimeout(() => {
      this.#waitTimer = undefined;
      // Taps made while a request is out add up, and go when it ends. See hold.
      if (!this.#requests) this.flushQuantity();
    }, 300);
  };

  /**
   * Sends every waiting change now.
   * @returns {Promise<unknown>} Resolves when the cart has caught up.
   */
  flushQuantity() {
    // A number being typed goes with a removal, so no box stays live while the rows move.
    if ([...this.#waiting.values()].some(({ quantity }) => quantity === 0)) this.#confirmTypedBox();

    clearTimeout(this.#waitTimer);
    this.#waitTimer = undefined;

    // Changes go first and removals last, from the bottom up, so rows of one variant keep their places.
    const waiting = [...this.#waiting.values()]
      .map((change) => ({ ...change, line: this.#lineOf(change.name) }))
      .sort(
        (a, b) =>
          Number(a.quantity === 0) - Number(b.quantity === 0) || (a.quantity === 0 ? b.line - a.line : a.line - b.line)
      );

    this.#waiting.clear();

    for (const { name, line, quantity } of waiting) {
      if (quantity === 0) {
        this.#removeLine(name, line);
        continue;
      }

      this.updateQuantity({
        name,
        quantity,
        action: 'change',
      });

      const textComponent = /** @type {TextComponent | undefined} */ (
        this.#rows[line - 1]?.querySelector('text-component')
      );
      textComponent?.shimmer();
    }

    return this.#chain;
  }

  /**
   * Handles the line item removal. A change still waiting for this row dies with it.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const row = this.#rows[Number(line) - 1];

    if (!row || row.classList.contains('removing') || row.style.display === 'none') return;
    if (this.#justMoved(Number(line))) return;

    // Leaving the box queues its number, so it goes first. After the 0 it would write over the removal.
    this.#confirmTypedBox();

    const name = this.#nameRow(row);

    this.#waiting.set(`${name.variant}:${name.place}`, { name, quantity: 0 });
    this.flushQuantity();
  }

  /**
   * Removes a line and plays the row's way out.
   * @param {RowName} name - The row's name.
   * @param {number} line - The row's line right now.
   */
  #removeLine(name, line) {
    this.updateQuantity({
      name,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#rows[line - 1];

    if (!cartItemRowToRemove) return;

    // The rows under this one slide up from now until 375 ms, while the request is still out.
    this.#movedFrom = performance.now() < this.#movedUntil ? Math.min(this.#movedFrom, line - 1) : line - 1;
    this.#movedUntil = Math.max(this.#movedUntil, performance.now() + 775);

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.#rows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) {
        row.style.display = 'none';
        return;
      }

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Hide the row after the animation ends. It stays in the page, so a failed request can bring it back; the redraw
      // takes it out. The redraw can also turn this row into the next line before the animation ends, and then the
      // class is gone and it stays visible.
      onAnimationEnd(row, () => {
        if (row.classList.contains('removing')) row.style.display = 'none';
      });
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {RowName} config.name - The row's name.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<unknown>} Resolves when this request and any before it have finished.
   */
  updateQuantity(config) {
    const version = this.#pageVersion;

    this.#queued.add(config);

    // A job dropped because the page was out of date never went, so a waiting Checkout tap stops.
    return this.hold(() => (version === this.#pageVersion ? this.#sendChange(config) : false));
  }

  /**
   * Runs work that changes the cart, one job at a time. A Checkout tap in the meantime waits for the last job to end.
   * @param {() => (Promise<unknown> | undefined)} work - The job. Resolving to false stops a waiting Checkout tap.
   * @returns {Promise<unknown>} Resolves with what the job resolved with. Never rejects.
   */
  hold(work) {
    if (!this.#requests) this.#stopCheckout = false;

    this.#requests++;
    this.#disableCartItems();

    const run = async () => {
      // The last redraw cleared this
      this.#disableCartItems();

      const result = await work();

      if (result === false) this.#stopCheckout = true;

      return result;
    };

    this.#chain = this.#chain
      .then(run)
      .catch((error) => {
        console.error(error);
        this.#stopCheckout = true;

        return false;
      })
      .finally(() => {
        this.#requests--;

        if (this.#requests) return;

        this.#enableCartItems();

        if (this.#waiting.size && !this.#waitTimer) {
          // Still the same run of jobs, so a failure so far must still stop a waiting tap. The flush's hold would clear it.
          const stop = this.#stopCheckout;

          this.flushQuantity();
          this.#stopCheckout = stop;
        }
      });

    return this.#chain;
  }

  /**
   * Sends one change and redraws from the answer.
   * @param {{ name: RowName, quantity: number, action: string }} config - The config.
   * @returns {Promise<unknown>} Never rejects.
   */
  #sendChange(config) {
    const { name, quantity } = config;
    const line = this.#lineOf(name);
    const key = this.#rows[line - 1]?.dataset.key;

    this.#queued.delete(config);

    // The row's gone or moved in a way that can't be followed, so the page is out of date.
    if (!key) return this.recover();

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: key,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetchWithin(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(({ status, text: responseText }) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // The store no longer has this line, so the page is out of date. Nothing to tell the customer, just redraw.
        if (status === 400 || /no valid id/i.test(String(parsedResponseText.errors ?? ''))) {
          throw new Error(`Line ${line} not found`);
        }

        if (parsedResponseText.errors) {
          return this.#handleCartError(line, parsedResponseText);
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (quantity === 0) {
          this.#confirmTypedBox();
          this.#closeUp(name);
        }

        return this.#morph(parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);

        return this.recover(line);
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * REAL FUEL 2026-11-01: redraws the cart. A redraw writes the store's numbers into every box, so what the shopper
   * has tapped or typed since goes back in afterwards.
   * @param {string} html - The section's HTML.
   * @returns {Promise<void>} Rejects if the HTML has no such section.
   */
  #morph(html) {
    const variants = (/** @type {ParentNode} */ root) =>
      [...root.querySelectorAll('[ref="cartItemRows[]"]')].map((row) => variantOf(/** @type {HTMLElement} */ (row)));

    // Rows are reused by place. If this redraw changes the rows, a live box would wake up on another flavour, so what it
    // holds is confirmed first, against the row it was typed in.
    if (
      this.#typedBox() &&
      variants(new DOMParser().parseFromString(html, 'text/html')).join() !== variants(this).join()
    ) {
      this.#confirmTypedBox();
    }

    const box = this.#typedBox();
    const typed = box && box.value !== box.defaultValue ? box.value : null;
    const row = box?.closest('[ref="cartItemRows[]"]');
    const variant = row instanceof HTMLElement ? variantOf(row) : null;

    const before = variants(this);

    const done = morphSection(this.sectionId, html);
    const after = variants(this);

    if (after.length < before.length) {
      const moved = before.findIndex((old, index) => old !== after[index]);

      this.#movedFrom = performance.now() < this.#movedUntil ? Math.min(this.#movedFrom, moved) : moved;
      this.#movedUntil = Math.max(this.#movedUntil, performance.now() + 400);
    }

    for (const { name, quantity } of [...this.#queued, ...this.#waiting.values()]) {
      const input = this.#rows[this.#lineOf(name) - 1]?.querySelector('input[name="updates[]"]');

      if (input instanceof HTMLInputElement) input.value = String(quantity);
    }

    // The redraw just took the way out off any row still waiting its turn to go, which would bring it back with live
    // buttons for a round trip. Hide it outright. Same task as the redraw, so it never paints, and its own answer takes it out.
    for (const { name, quantity } of this.#queued) {
      const going = quantity === 0 ? this.#rows[this.#lineOf(name) - 1] : null;

      if (!going) continue;

      [going, ...this.#rows.filter((other) => other.dataset.parentKey === going.dataset.key)].forEach((other) => {
        other.style.display = 'none';
      });
    }

    // Only if the box still belongs to the same flavour.
    if (box && typed !== null && row instanceof HTMLElement && row.isConnected && variantOf(row) === variant) {
      box.value = typed;
    }

    return done;
  }

  /**
   * REAL FUEL 2026-11-02: a removal landed, so its variant has one row fewer and the rows under it moved up. Changes still
   * to go follow their rows, so the next removal of a twin line isn't taken for a page out of date.
   * @param {RowName} gone - The name of the row that went.
   */
  #closeUp(gone) {
    const waiting = [...this.#waiting.values()];

    for (const { name } of [...this.#queued, ...waiting]) {
      if (name.variant !== gone.variant) continue;

      // A change to the row that went must not slide onto the row under it, so it now finds nothing.
      name.of = name.place === gone.place ? 0 : name.of - 1;
      if (name.place > gone.place) name.place--;
    }

    this.#waiting.clear();

    for (const change of waiting) {
      if (change.name.of) this.#waiting.set(`${change.name.variant}:${change.name.place}`, change);
    }
  }

  /**
   * REAL FUEL 2026-11-01: redraws this section from the live cart and refreshes the header count.
   * Rejects if the redraw did not happen.
   * @returns {Promise<void>}
   */
  async refresh() {
    const url = new URL(window.location.href);

    url.searchParams.set('section_id', this.sectionId);

    const section = await fetchWithin(url, { cache: 'no-store' });

    if (!section.ok) throw new Error(`HTTP ${section.status}`);

    await this.#morph(section.text);

    this.#unsure = false;

    const cart = await fetchWithin(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: JSON.parse(cart.text).item_count,
        source: 'cart-items-component',
      })
    );

    // snippets/rf-cart-boost.liquid settles a mixed cart it finds here
    document.dispatchEvent(new CustomEvent('rf:cart-redrawn'));
  }

  /**
   * REAL FUEL 2026-11-01: stock Horizon only logged a failed request, so a removed row stayed gone while the
   * cart still held it. Show what the cart holds instead, trying once more if the first redraw fails.
   * @param {number} [line] - The line whose change failed, if any.
   * @returns {Promise<false>} False, so a waiting Checkout tap stops and the shopper sees the page.
   */
  recover(line) {
    // These taps are about to be thrown away. Queued ones stop by themselves when their jobs are dropped.
    if (this.#waiting.size) this.#stopCheckout = true;

    this.#pageVersion++;
    this.#waiting.clear();
    this.#queued.clear();
    clearTimeout(this.#waitTimer);
    this.#waitTimer = undefined;
    resetShimmer(this);

    return this.refresh()
      .catch(() => new Promise((resolve) => setTimeout(resolve, 3000)).then(() => this.refresh()))
      .then(
        () => /** @type {false} */ (false),
        (error) => this.#giveUp(error, line)
      );
  }

  /**
   * The redraw failed twice. If the store answered, a fresh page load can still sort it out. With no signal a reload
   * would only swap the cart for the browser's error page, so the page stays up and shows what it last knew.
   * @param {Error & { noAnswer?: boolean }} error - The last failure.
   * @param {number} [line] - The line whose change failed, if any.
   * @returns {Promise<false> | false} False, or a promise that never ends, so the cart holds still until the page goes.
   */
  #giveUp(error, line) {
    if (!error.noAnswer) {
      window.location.reload();

      return new Promise(() => {});
    }

    this.#unsure = true;
    this.#waiting.clear();
    this.#queued.clear();
    clearTimeout(this.#waitTimer);
    this.#waitTimer = undefined;

    this.#rows.forEach((row) => {
      const input = row.querySelector('input[name="updates[]"]');

      row.classList.remove('removing');
      row.style.removeProperty('display');
      if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    });

    if (line) this.#showRowError(line, 'No signal. Tap again when you are back online.');

    return false;
  }

  /**
   * @returns {string} Each row's key and quantity, to compare with the live cart.
   */
  #shown() {
    return this.#rows
      .map((row) => {
        const input = row.querySelector('input[name="updates[]"]');

        return `${row.dataset.key}:${input instanceof HTMLInputElement ? input.defaultValue : ''}`;
      })
      .join();
  }

  /**
   * REAL FUEL 2026-11-01: on arrival, on coming back to this tab and when the signal returns, redraw if the cart is
   * not what the page shows.
   * @param {Event} [event] - What woke the check. None on arrival.
   */
  #checkLiveCart = (event) => {
    const busy = () => document.visibilityState !== 'visible' || this.#requests || this.#waiting.size;

    if (busy()) return;

    const asked = this.#shown();

    fetchWithin(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(({ text }) => {
        const live = JSON.parse(text)
          .items.map((/** @type {{ key: string, quantity: number }} */ item) => `${item.key}:${item.quantity}`)
          .join();

        if (live === this.#shown()) {
          this.#unsure = false;
          return;
        }

        // The page moved while this was out, so the answer is old.
        if (busy() || this.#shown() !== asked) return;

        // A page first drawn empty never ran the full cart's scripts, and a redraw can't run them. The check on arrival
        // reloads one time only, so a page drawn from just before an add can't go round and round. The other checks
        // take a new event each time, so those can reload whatever started this page.
        const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
          performance.getEntriesByType('navigation')[0]
        );

        if (!this.#rows.length && (event || navigation?.type !== 'reload')) {
          window.location.reload();
          return;
        }

        return this.hold(() => this.refresh().catch(() => {}));
      })
      .catch(() => {});
  };

  /**
   * REAL FUEL 2026-11-01: Back or Forward can bring back a frozen copy of this page, with the same old rows. The phone
   * can say online on dead bars or captive wifi, so only the store can say the signal is real. The redraw asks it.
   * With no answer the page stays up, and the next Checkout tap redraws first.
   * @param {PageTransitionEvent} event - The event.
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    // A tile tap holds the cart still until the page goes.
    if (!this.#requests) this.#enableCartItems();

    this.#unsure = true;

    // A page with no rows never ran the full cart's scripts, so only the live check can reload it.
    if (!this.#rows.length) {
      this.#checkLiveCart(event);
      return;
    }

    // Its rows, tiles and Delivery plans can all be old, so nothing acts on them until this redraw. Tiles go inert, rows
    // and Delivery queue behind it. A Checkout tap waiting on it stops if the redraw changed the page or failed.
    const before = this.#shown();

    this.hold(() =>
      this.refresh().then(
        () => (this.#shown() === before ? undefined : false),
        () => false
      )
    );
  };

  /**
   * REAL FUEL 2026-11-01: every change goes by /cart/change.js, so Checkout has nothing to write. Its boxes go by
   * position, so an out-of-date page would put old quantities on the wrong lines.
   * @param {FormDataEvent} event - The event.
   */
  #onFormData = (event) => {
    event.formData.delete('updates[]');
  };

  /**
   * @returns {HTMLInputElement | null} The cart's quantity box with focus, if any.
   */
  #typedBox() {
    const box = document.activeElement;

    return box instanceof HTMLInputElement && box.name === 'updates[]' && this.contains(box) ? box : null;
  }

  /**
   * Confirms a number being typed. Leaving the box is what queues it.
   * @param {Element} [tapped] - What was tapped, if that is why.
   */
  #confirmTypedBox(tapped) {
    const box = this.#typedBox();

    if (!box || box === tapped) return;

    box.blur();
    // The caret can stay in the box after a blur, and a redraw can give that box to another flavour.
    window.getSelection?.()?.removeAllRanges();
  }

  /**
   * The header count kept in sessionStorage is old once a tile is tapped.
   */
  #forgetCount() {
    try {
      sessionStorage.removeItem('cart-count');
    } catch (_) {
      // no-op
    }
  }

  /**
   * REAL FUEL 2026-11-01: the add tiles are plain links, and a change still waiting would die with the page, so it
   * goes first. A Checkout tap while the cart is updating waits for it and then goes, unless a change failed. Then the
   * shopper sees the page first, and the next tap goes.
   * @param {MouseEvent} event - The event.
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // A tap elsewhere confirms a typed number, whatever the browser does with focus.
    this.#confirmTypedBox(event.target);

    const link = event.target.closest('a.rf-boost__add');

    if (link instanceof HTMLAnchorElement) {
      // One add per tile, however fast the second tap comes.
      if (performance.now() < this.#tileTapUntil || this.settling()) {
        event.preventDefault();
        return;
      }

      this.#tileTapUntil = performance.now() + 3000;
      this.#forgetCount();

      if (!this.#waiting.size) return;

      event.preventDefault();

      // The redraw can point this link at another product.
      const { href } = link;

      this.flushQuantity().then(() => {
        if (this.#stopCheckout) {
          this.#tileTapUntil = 0;
          return;
        }

        this.#forgetCount();
        this.#disableCartItems();
        window.location.href = href;
      });
      return;
    }

    const checkout = event.target.closest('button[name="checkout"]');

    if (!(checkout instanceof HTMLButtonElement)) return;

    if (this.#unsure && !this.#requests) {
      const before = this.#shown();

      this.hold(() =>
        this.refresh().then(
          () => (this.#shown() === before ? undefined : false),
          (error) => this.#giveUp(error)
        )
      );
    }

    if (this.#waiting.size) this.flushQuantity();

    if (!this.#requests) return;

    event.preventDefault();

    if (this.#checkoutWaiting) return;

    this.#checkoutWaiting = true;
    checkout.setAttribute('aria-busy', 'true');

    this.#chain.then(() => {
      checkout.removeAttribute('aria-busy');

      if (!this.#stopCheckout) {
        this.#checkoutWaiting = false;
        (checkout.isConnected ? checkout : this.querySelector('button[name="checkout"]'))?.click();
        return;
      }

      // Seen once, so the next tap goes.
      this.#stopCheckout = false;

      // A job behind this one would redraw the button and wipe the note straight away.
      this.#chain.then(() => {
        this.#checkoutWaiting = false;

        const label = checkout.textContent;
        const note = 'Check cart, tap again';

        checkout.textContent = note;
        setTimeout(() => {
          if (checkout.textContent === note) checkout.textContent = label;
        }, 4000);
      });
    });
  };

  /**
   * REAL FUEL 2026-11-01: Enter in a quantity box only confirms the number. The browser would press Check out.
   * @param {KeyboardEvent} event - The event.
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    if (event.target.name !== 'updates[]') return;

    event.preventDefault();
    event.target.blur();
  };

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Shows a note under a row.
   * @param {number} line - The line.
   * @param {string} text - The note.
   */
  #showRowError(line, text) {
    const error = this.querySelector(`[ref="cartItemError-${line}"]`);
    const container = this.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    if (error instanceof HTMLElement && container instanceof HTMLElement) {
      error.textContent = text;
      container.classList.remove('hidden');
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   * @returns {Promise<false>} Resolves when the section has been redrawn. False, so a waiting Checkout tap stops.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.#rows[line - 1]?.querySelector('input[name="updates[]"]');

    if (!(quantityInput instanceof HTMLInputElement)) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    this.#showRowError(line, parsedResponseText.errors);

    // REAL FUEL 2026-09-02: Shopify can PARTIALLY apply a change and still answer
    // with an error (typing 999 caps the line at what is available and returns
    // "Only 50 items were added"). Stock Horizon only reset the input, so the page
    // kept showing the old quantity and the old total while the live cart held
    // the capped quantity, and checkout charged for it. Re-render this section
    // from the live cart so the page always shows what the cart actually holds,
    // refresh the header count, then re-show the error on the fresh markup.
    return this.refresh()
      .catch(() => this.recover(line))
      .then(() => {
        this.#showRowError(line, parsedResponseText.errors);

        return /** @type {false} */ (false);
      });
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morph(cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
